import type { DataToSendEvent } from '@core/core/types'
import { useSpa } from '@core/hooks'
import type { Nullable } from 'common/types'
import { useEffect, useState } from 'react'

import type {
  AddingFilesModalContainerPropsType,
  FormDataType,
  OnSaveFormData,
  UseAddingFilesTypeReturn,
} from './types'

export const useAddingFiles = (
  files: Nullable<AddingFilesModalContainerPropsType['files']>,
  data?: DataToSendEvent,
  fileAdd?: (file: File, formData?: OnSaveFormData) => void,
  filesAdd?: (files: File[], formData?: OnSaveFormData) => void,
  setMainFileName?: (value?: string) => void,
): UseAddingFilesTypeReturn => {
  const sendSpaEvent = useSpa()
  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [filesDownload, setFilesDownload] = useState<File[]>([])
  const [activeFileReady, setActiveFileReady] = useState<File | undefined>(undefined)

  useEffect(() => {
    if (files?.length) {
      setFilesDownload(files)
    }
  }, [files])

  useEffect(() => {
    if (filesDownload.length && !activeFileReady) {
      setActiveFileReady(filesDownload[0])
      setVisibleModal(true)
    }
  }, [filesDownload])

  const handleModalClose = () => {
    setVisibleModal(false)
  }

  useEffect(() => {
    if (!visibleModal) {
      setActiveFileReady(undefined)
      setMainFileName?.(undefined)
    }
  }, [visibleModal])

  const handleNextFile = (updateCurrentObject: boolean, formData?: OnSaveFormData) => {
    if (filesDownload.length) {
      const filesDownloadNew = filesDownload.slice(1)
      if (updateCurrentObject && formData) {
        filesAdd?.([filesDownload[0]], formData)
        fileAdd?.(filesDownload[0], formData)
      }

      setFilesDownload(filesDownloadNew)
      setActiveFileReady(filesDownloadNew[0])

      if (!filesDownloadNew.length) {
        handleModalClose()
      }
    }
  }

  const onCancel = () => {
    handleNextFile(false)
  }

  const onSave = (formData?: OnSaveFormData) => {
    if (data) {
      sendSpaEvent(data.id, data.params)
    }
    setMainFileName?.(formData?.objectName)
    handleNextFile(true, formData)
  }

  const onSaveForAll = (formData?: FormDataType) => {
    if (formData) {
      if (filesAdd) {
        filesAdd(filesDownload, formData)
      } else {
        filesDownload.forEach(file => {
          const newFormData = { ...formData, objectName: file.name }
          fileAdd?.(file, newFormData)
        })
      }
    }

    setFilesDownload([])
    handleModalClose()
  }

  return {
    visibleModal,
    activeFileReady,
    isOneFileDownloaded: filesDownload.length > 1,
    onCancel,
    onSave,
    onSaveForAll,
  }
}
